import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type RowActionsProps, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { AcknowledgementDefinitionsListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { client } from "@/hono";
import { EditAcknowledgementDefinitionModal } from "@/modals/organizations/edit-acknowledgement-definition-modal";

export function RowActions({
	row,
	isProjectContext,
}: RowActionsProps<AcknowledgementDefinitionsListItem> & { isProjectContext: boolean }) {
	/** Context */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();

	/** Hooks */
	const asyncModal = useAsyncModal();
	const editDisclosure = useDisclosure();

	/** Guard */
	if (row.original.default && isProjectContext) return <Fragment />;

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.organizations["acknowledgement-definitions"][
			":acknowledgementDefinitionId"
		].$delete({
			param: { acknowledgementDefinitionId: row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				<Menu
					items={createStandardMenuItems({
						onEdit: editDisclosure.onOpen,
						onDelete: handleOnDelete,
					})}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
					/>
				</Menu>
			</RowActionsWrapper>
			{editDisclosure.isOpen && (
				<EditAcknowledgementDefinitionModal
					isOpen={editDisclosure.isOpen}
					onClose={editDisclosure.onClose}
					acknowledgementDefinitionId={row.original.id}
					onComplete={refetch}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t(
						"organization.worksite_acknowledgement_definitions.delete_confirmation_message",
					)}
					controller={asyncModal}
				/>
			)}
		</Fragment>
	);
}
